/**
 * EN/AR i18n. Keys are "<module>.<key>" and live in catalogs/<module>/{en,ar}.json.
 * Owner: ui-shell (foundation seeded). Every en key must exist in ar (CI test).
 */
import { CATALOGS, type Catalog } from './catalogs';
import { applyOverride } from './text-overrides';

export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';
export const RTL_LOCALES: readonly Locale[] = ['ar'];

export const isRtl = (l: Locale) => RTL_LOCALES.includes(l);
export const dirFor = (l: Locale): 'rtl' | 'ltr' => (isRtl(l) ? 'rtl' : 'ltr');
export const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (LOCALES as readonly string[]).includes(v);

/** Pick a locale from an Accept-Language header or explicit preference. */
export function negotiateLocale(pref?: string | null, acceptLanguage?: string | null): Locale {
  if (isLocale(pref)) return pref;
  for (const part of (acceptLanguage ?? '').split(',')) {
    const tag = part.trim().split(';')[0]?.slice(0, 2).toLowerCase();
    if (isLocale(tag)) return tag;
  }
  return DEFAULT_LOCALE;
}

const merged: Record<Locale, Catalog> = { en: {}, ar: {} };
for (const c of Object.values(CATALOGS)) {
  Object.assign(merged.en, c.en);
  Object.assign(merged.ar, c.ar);
}

export type Translate = (key: string, params?: Record<string, string | number>) => string;

const interpolate = (raw: string, params?: Record<string, string | number>) =>
  params ? raw.replace(/\{(\w+)\}/g, (_, p: string) => String(params[p] ?? `{${p}}`)) : raw;

/** Keys that fell back to English at runtime (dev overlay + tests). */
export const runtimeMissing = new Set<string>();

/** Translate with EN fallback (published staff overrides first, see text-overrides.ts). A missing key is recorded and reported via onMissing (QM-D08-001). */
export function createTranslator(locale: Locale, onMissing?: (key: string, locale: Locale) => void): Translate {
  return (key, params) => {
    let raw = applyOverride(locale, key, merged[locale][key]);
    if (raw === undefined) {
      runtimeMissing.add(`${locale}:${key}`);
      onMissing?.(key, locale);
      raw = applyOverride('en', key, merged.en[key]) ?? key;
    }
    return interpolate(raw, params);
  };
}

/** Translator over explicit catalogs (tests, previews). Same fallback rules as createTranslator. */
export function translatorFor(catalog: { en: Catalog; ar: Catalog }, locale: Locale, onMissing?: (key: string) => void): Translate {
  return (key, params) => {
    let raw = applyOverride(locale, key, catalog[locale][key]);
    if (raw === undefined) {
      onMissing?.(key);
      raw = applyOverride('en', key, catalog.en[key]) ?? key;
    }
    return interpolate(raw, params);
  };
}

/** Format numbers/dates with the locale (Arabic uses latn digits by default for data tables). */
export const formatNumber = (l: Locale, n: number, o?: Intl.NumberFormatOptions) => new Intl.NumberFormat(l === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE', o).format(n);
export const formatDate = (l: Locale, d: Date, o?: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(l === 'ar' ? 'ar-AE-u-ca-gregory-nu-latn' : 'en-AE-u-ca-gregory', o).format(d);

export { CATALOGS, type Catalog };
export * from './format';
export * from './bidi';
export * from './missing';
export * from './document';
export * from './text-overrides';
